<div class="process-page">
  <div class="page-header">
    <app-back-button
      parentRoute="/admin/process-management/processes"
      [title]="pageHeading().title"
      [subtitle]="pageHeading().subtitle"
    ></app-back-button>
    <div class="header-actions">
      @if (process()) { <app-process-parameter-defaults [processKey]="processKey()" /> }
      <app-secondary-button
        [title]="executionHoldTitle()"
        [icon]="executionHoldIcon()"
        size="small"
        [disable]="!process() || executionHoldSaving()"
        [loading]="executionHoldSaving()"
        (action)="requestExecutionHold()"
      ></app-secondary-button>
      @if (process()?.key === 'ai.classify-crops-onnx') {
        <app-onnx-process-model />
      }
      <app-secondary-button
        title="Edit assignment cap"
        icon="pi pi-sliders-h"
        size="small"
        [disable]="!process() || capLoading() || capSaving()"
        [loading]="capLoading()"
        (action)="requestEditCap()"
      ></app-secondary-button>
      <app-secondary-button
        title="Edit worker tags"
        icon="pi pi-tag"
        size="small"
        [disable]="!process() || tagsLoading() || tagsSaving()"
        [loading]="tagsLoading()"
        (action)="requestEditTags()"
      ></app-secondary-button>
      <app-secondary-button
        title="If the assignment fails"
        icon="pi pi-replay"
        size="small"
        [disable]="!process() || failureSaving()"
        (action)="requestEditFailure()"
      ></app-secondary-button>
      <app-secondary-button
        title="Notify people"
        icon="pi pi-bell"
        size="small"
        [disable]="!process()"
        (action)="notifyPeople()"
      ></app-secondary-button>
    </div>
  </div>

  @if (errorMessage()) {
    <div class="error-message">{{ errorMessage() | translate }}</div>
  }

  @if (process(); as selectedProcess) {
    <header class="process-heading" [class.process-card--failed]="processActivity()?.hasError">
      <div>
        <h2>{{ (selectedProcess.displayName || selectedProcess.key) | translate }}</h2>
        @if (selectedProcess.description) {
          <p class="process-description">{{ selectedProcess.description | translate }}</p>
        }
        <app-copyable-identifier [value]="selectedProcess.key"></app-copyable-identifier>
        <app-command-config-links [processKey]="selectedProcess.key"></app-command-config-links>
        <span class="process-version">{{ 'Version' | translate }} {{ selectedProcess.version }}</span>
        <app-process-admission-facts
          [maxConcurrentAssignments]="selectedProcess.maxConcurrentAssignments"
          [tags]="selectedProcess.requiredWorkerTags"
        ></app-process-admission-facts>
        @if (selectedProcess.executionSuspended) {
          <span class="process-stopped">{{ 'Process stopped' | translate }}</span>
        }
        <div class="failure-facts">
          <span class="failure-facts__item">
            <span class="failure-facts__label">{{ 'When the run is interrupted' | translate }}</span>
            <span>{{ failurePolicyLabel() | translate }}</span>
          </span>
          <span class="failure-facts__item">
            <span class="failure-facts__label">{{ 'Resume attempts' | translate }}</span>
            <span>{{ failureResumeLimit() }}</span>
          </span>
          <span class="failure-facts__item">
            <span class="failure-facts__label">{{ 'Pause after consecutive failures' | translate }}</span>
            <span>{{ failurePauseAfter() }}</span>
          </span>
        </div>
        <app-orchestration-process-activity [activity]="processActivity()"></app-orchestration-process-activity>
        <app-orchestration-process-events [events]="publishedEvents()"></app-orchestration-process-events>
      </div>
    </header>

    @if (detailsTabs().length) {
      <app-tabs
        [tabs]="detailsTabs()"
        [activeTab]="activeTab()"
        (tabChanged)="onTabChange($event)"
      ></app-tabs>
    }
  } @else if (loading()) {
    <app-loading [loading]="true"></app-loading>
  } @else if (notFound()) {
    <div class="empty-state">{{ 'Process not found' | translate }}</div>
  }
</div>

<ng-template #assignmentsTemplate>
  <section class="assignments-panel orch-table-panel">
    <app-orchestration-table-heading heading="Assignments" queryParam="assignments" [(query)]="assignmentSearch">
      <div tableActions class="table-actions">
        <app-primary-button
          title="Create assignment"
          icon="pi pi-plus"
          size="small"
          [disable]="!process()"
          (action)="createAssignment()"
        ></app-primary-button>
        <app-secondary-button
          title="Stop all assignments"
          icon="pi pi-stop-circle"
          size="small"
          [disable]="!process() || stopAllSaving()"
          [loading]="stopAllSaving()"
          (action)="requestStopAll()"
        ></app-secondary-button>
        <app-secondary-button
          title="ORCH_RESTART_FAILED_AND_SKIPPED"
          icon="pi pi-replay"
          size="small"
          [disable]="!process() || restartFailedSaving()"
          [loading]="restartFailedSaving()"
          (action)="requestRestartFailed()"
        ></app-secondary-button>
        <app-secondary-button
          title="Refresh data"
          icon="refresh"
          size="small"
          [disable]="loading()"
          [loading]="loading()"
          (action)="reload()"
        ></app-secondary-button>
      </div>
    </app-orchestration-table-heading>
    @if (processRssReady()) {
      <section class="process-rss">
        <h3 class="process-rss__title">{{ 'RSS of one task' | translate }}</h3>
        @if (processRss(); as rss) {
          <dl class="process-rss__figures">
            <div>
              <dt>{{ 'Average RSS' | translate }}</dt>
              <dd>{{ rss.average }}</dd>
            </div>
            <div>
              <dt>{{ 'Minimum RSS' | translate }}</dt>
              <dd>{{ rss.minimum }}</dd>
            </div>
            <div>
              <dt>{{ 'Maximum RSS' | translate }}</dt>
              <dd>{{ rss.maximum }}</dd>
            </div>
          </dl>
          <p class="process-rss__note">{{ 'Last five assignments' | translate }}</p>
        } @else {
          <p class="empty-state compact">{{ 'RSS is recorded from the next assignment' | translate }}</p>
        }
      </section>
    }
    <div class="assignment-toolbar">
      <div class="assignment-toolbar__group">
        <app-assignment-result-filter
          [selected]="resultFilter()"
          (selectedChange)="onResultFilterChange($event)"
        ></app-assignment-result-filter>
      </div>
      <div class="assignment-toolbar__group state-filters">
        @for (chip of stateChips(); track chip.id) {
          <button
            type="button"
            class="filter-chip"
            [class.active]="chip.selected"
            (click)="selectStateFilter(chip.id)">
            <span>{{ chip.label | translate }}</span>
          </button>
        }
      </div>
      <div class="assignment-toolbar__group">
        <app-date-picker
          [ngModel]="createdRange()"
          (ngModelChange)="onCreatedRangeChange($event)"
          selectionMode="range"
          [showClear]="true"
          [placeholder]="'Created' | translate"
        ></app-date-picker>
      </div>
    </div>
    <app-data-table
      [columns]="assignmentColumns()"
      [data]="assignmentRows()"
      [config]="assignmentTableConfig()"
      [loading]="assignmentLoading()"
      (onRowClick)="openAssignment($event)"
    ></app-data-table>
    <app-custom-paginator
      [first]="assignmentFirst()"
      [pageSize]="assignmentPageSize()"
      [totalRecords]="assignmentTotal()"
      [rowsPerPageOptions]="assignmentPageSizeOptions"
      (pageChange)="onAssignmentPageChange($event)"
      (pageSizeChange)="onAssignmentPageSizeChange($event)"
    ></app-custom-paginator>
  </section>
</ng-template>

<ng-template #subscriptionsTemplate>
  <section class="subscriptions-panel orch-table-panel">
    <app-orchestration-table-heading heading="Subscriptions" [(query)]="subscriptionSearch">
      <div tableActions class="table-actions">
        <app-secondary-button
          title="Create subscription"
          icon="pi pi-calendar-plus"
          size="small"
          [disable]="!process()"
          (action)="createSubscription()"
        ></app-secondary-button>
        <app-secondary-button
          title="Refresh data"
          icon="refresh"
          size="small"
          [disable]="loading()"
          [loading]="loading()"
          (action)="reload()"
        ></app-secondary-button>
      </div>
    </app-orchestration-table-heading>
    <app-data-table
      [columns]="subscriptionColumns()"
      [data]="visibleSubscriptions()"
      [config]="subscriptionTableConfig"
      [loading]="loading()"
      (onRowClick)="openSubscription($event)"
    ></app-data-table>
  </section>
</ng-template>

<ng-template #triggersTemplate>
  <section class="triggers-panel orch-table-panel">
    <app-orchestration-table-heading heading="Event triggers" [(query)]="triggerSearch">
      <div tableActions class="table-actions">
        <app-secondary-button
          title="Refresh data"
          icon="refresh"
          size="small"
          [disable]="loading()"
          [loading]="loading()"
          (action)="reload()"
        ></app-secondary-button>
      </div>
    </app-orchestration-table-heading>
    <app-data-table
      [columns]="triggerColumns()"
      [data]="visibleTriggers()"
      [config]="triggerTableConfig"
      [loading]="loading()"
    ></app-data-table>
  </section>
</ng-template>

<ng-template #templatesTemplate>
  <section class="templates-panel orch-table-panel">
    <app-data-table
      [columns]="templateColumns"
      [data]="templateRows()"
      [config]="templateTableConfig"
      [loading]="loading()"
    ></app-data-table>
    <app-notification-template-fields
      [events]="templateFieldEvents()"
      [templates]="notificationTemplates()"
      [dialogOnly]="true"
      [openedTemplateId]="expandedTemplateRowId()"
      (templateClosed)="expandedTemplateRowId.set(null)"
      (templateSaved)="reload()"
    ></app-notification-template-fields>
  </section>
</ng-template>

<ng-template #subscribersTemplate>
  <section class="subscribers-panel orch-table-panel">
    <app-orchestration-table-heading heading="Notification subscribers" [(query)]="subscriberSearch">
      <div tableActions class="table-actions">
        <app-secondary-button
          title="Refresh data"
          icon="refresh"
          size="small"
          [disable]="loading()"
          [loading]="loading()"
          (action)="reload()"
        ></app-secondary-button>
      </div>
    </app-orchestration-table-heading>
    <app-data-table
      [columns]="subscriberColumns"
      [data]="visibleSubscribers()"
      [config]="subscriberTableConfig"
      [loading]="loading()"
      (onRowClick)="openSubscriber($event)"
    ></app-data-table>
  </section>
</ng-template>

<ng-template #accessTemplate>
  <section class="access-panel orch-table-panel">
    <app-orchestration-table-heading heading="access.tab.grants" [showSearch]="false">
      <div tableActions class="table-actions">
        <app-secondary-button
          title="Refresh data"
          icon="refresh"
          size="small"
          (action)="reloadAccess()"
        ></app-secondary-button>
      </div>
    </app-orchestration-table-heading>
    <p class="database-hint">{{ 'access.process.hint' | translate }}</p>
    <app-data-table [columns]="accessColumns" [data]="accessRows()" [config]="accessTableConfig"></app-data-table>
  </section>
</ng-template>

<ng-template #databaseTemplate>
  <section class="database-panel">
    <p class="database-hint">{{ 'Database actions hint' | translate }}</p>
    <div class="orch-table-panel">
    <app-orchestration-table-heading heading="Expected database actions" [(query)]="dataEffectSearch">
      <div tableActions class="table-actions">
        <app-secondary-button
          title="Refresh data"
          icon="refresh"
          size="small"
          [disable]="loading()"
          [loading]="loading()"
          (action)="reload()"
        ></app-secondary-button>
      </div>
    </app-orchestration-table-heading>
    <app-data-table
      [columns]="dataEffectColumns()"
      [data]="visibleDataEffects()"
      [config]="dataEffectTableConfig"
      [loading]="loading()"
    ></app-data-table>
    </div>
    <p class="database-hint">{{ 'Object storage actions hint' | translate }}</p>
    <div class="orch-table-panel">
    <app-orchestration-table-heading heading="Expected object-storage actions" [(query)]="storageEffectSearch">
    </app-orchestration-table-heading>
    <app-data-table
      [columns]="storageEffectColumns()"
      [data]="visibleStorageEffects()"
      [config]="storageEffectTableConfig"
      [loading]="loading()"
    ></app-data-table>
    </div>
  </section>
</ng-template>

<ng-template #assignmentNameTemplate let-row>
  <div class="name-cell">
    <strong>{{ row.name | translate }}</strong>
    @if (row.error) {
      <small>{{ row.error }}</small>
    }
  </div>
</ng-template>

<ng-template #assignmentStateTemplate let-row>
  <div class="status-cell">
    <p-tag [value]="row.stateLabel | translate" [severity]="row.stateSeverity"></p-tag>
    @if (row.statusAt) {
      <small>{{ row.statusAt | dateFormatLang:translate.currentLang:true }}</small>
    }
  </div>
</ng-template>

<ng-template #assignmentHostTemplate let-row>
  <app-assignment-host-label
    [serviceName]="row.workerServiceName"
    [hostName]="row.workerHostName"
    [workerId]="row.workerId"
    [fallback]="row.hostLabel"
  ></app-assignment-host-label>
</ng-template>

<ng-template #assignmentCountTemplate let-row>
  <app-assignment-result-cell
    [resultCount]="row.resultCount"
    [tooltip]="row.resultTooltip"
  ></app-assignment-result-cell>
</ng-template>

<ng-template #assignmentRssTemplate let-row>
  <app-assignment-rss-cell [rss]="row.rss"></app-assignment-rss-cell>
</ng-template>

<ng-template #statusTagTemplate let-row>
  <p-tag [value]="row.statusLabel | translate" [severity]="row.statusSeverity"></p-tag>
</ng-template>

<ng-template #lastAssignmentTemplate let-row>
  <app-copyable-identifier [value]="row.lastAssignmentId || ''" [link]="row.lastAssignmentUrl || null"></app-copyable-identifier>
</ng-template>

<ng-template #triggerIdentityTemplate let-row>
  <app-orchestration-identity-cell [name]="row.name" [lines]="row.identityLines"></app-orchestration-identity-cell>
</ng-template>

<ng-template #triggerDirectionTemplate let-row>
  <span>{{ row.direction | translate }}</span>
</ng-template>

<ng-template #triggerExecutionsTemplate let-row>
  <div class="executions-cell">
    <strong>{{ row.executionCount }}</strong>
    <small>
      {{ 'Succeeded' | translate }}: {{ row.successfulExecutionCount }},
      {{ 'Failed' | translate }}: {{ row.failedExecutionCount }}
    </small>
  </div>
</ng-template>

<ng-template #dataEffectCommandTemplate let-row>
  <app-copyable-identifier [value]="row.commandLabel" [link]="row.commandUrl"></app-copyable-identifier>
</ng-template>

<ng-template #dataEffectOperationTemplate let-row>
  <p-tag [value]="row.operation | translate" [severity]="row.operationSeverity"></p-tag>
</ng-template>

<ng-template #dataEffectDescriptionTemplate let-row>
  <span>{{ row.description | translate }}</span>
</ng-template>

<ng-template #assignmentActionsTemplate let-row>
  <div class="row-actions" (click)="$event.stopPropagation()">
    @if (row.canRestart) {
      <app-secondary-button
        title="Restart assignment"
        icon="pi pi-replay"
        size="small"
        (action)="requestRestart(row, $event)"
      ></app-secondary-button>
    }
    @if (row.canCancel) {
      <app-secondary-button
        title="Cancel assignment"
        icon="pi pi-stop-circle"
        size="small"
        (action)="requestCancellation(row, $event)"
      ></app-secondary-button>
    }
  </div>
</ng-template>

<p-dialog [resizable]="false" [draggable]="false"
  [visible]="createVisible()"
  (visibleChange)="onCreateVisibleChange($event)"
  [modal]="true"
  [style]="{ width: 'min(960px, 96vw)' }"
  [header]="'Create assignment' | translate"
  [closable]="!createSaving()"
  [closeOnEscape]="!createSaving()">
  @if (createError()) {
    <div class="error-message">{{ createError() | translate }}</div>
  }
  <app-assignment-start-form [processKey]="processKey()"></app-assignment-start-form>
  <ng-template #footer>
    <app-secondary-button
      title="Cancel"
      [disable]="createSaving()"
      (action)="closeCreate()"
    ></app-secondary-button>
    <app-primary-button
      title="Run"
      [loading]="createSaving()"
      [disable]="!canCreateAssignment() || createSaving()"
      (action)="confirmCreate()"
    ></app-primary-button>
  </ng-template>
</p-dialog>

<p-dialog [resizable]="false" [draggable]="false"
  [visible]="restartVisible()"
  (visibleChange)="restartVisible.set($event)"
  [modal]="true"
  [style]="{ width: 'min(520px, 94vw)' }"
  [header]="'Restart assignment' | translate">
  <p>{{ 'Restart assignment confirmation' | translate }}</p>
  <ng-template #footer>
    <app-secondary-button
      title="Keep as-is"
      [disable]="restartSaving()"
      (action)="closeRestart()"
    ></app-secondary-button>
    <app-primary-button
      title="Restart assignment"
      icon="pi pi-replay"
      [loading]="restartSaving()"
      (action)="confirmRestart()"
    ></app-primary-button>
  </ng-template>
</p-dialog>

<p-dialog [resizable]="false" [draggable]="false"
  [visible]="stopAllVisible()"
  (visibleChange)="stopAllVisible.set($event)"
  [modal]="true"
  [style]="{ width: 'min(520px, 94vw)' }"
  [header]="'Stop all assignments' | translate">
  <p>{{ 'Stop all assignments confirmation' | translate }}</p>
  <ng-template #footer>
    <app-secondary-button
      title="Keep running"
      [disable]="stopAllSaving()"
      (action)="closeStopAll()"
    ></app-secondary-button>
    <app-primary-button
      title="Stop all assignments"
      icon="pi pi-stop-circle"
      [loading]="stopAllSaving()"
      (action)="confirmStopAll()"
    ></app-primary-button>
  </ng-template>
</p-dialog>

<p-dialog [resizable]="false" [draggable]="false"
  [visible]="executionHoldVisible()"
  (visibleChange)="executionHoldVisible.set($event)"
  [modal]="true"
  [style]="{ width: 'min(520px, 94vw)' }"
  [header]="executionHoldTitle() | translate">
  <p>{{ executionHoldConfirmation() | translate }}</p>
  <ng-template #footer>
    <app-secondary-button
      [title]="executionHoldDismissTitle()"
      [disable]="executionHoldSaving()"
      (action)="closeExecutionHold()"
    ></app-secondary-button>
    <app-primary-button
      [title]="executionHoldTitle()"
      [icon]="executionHoldIcon()"
      [loading]="executionHoldSaving()"
      (action)="confirmExecutionHold()"
    ></app-primary-button>
  </ng-template>
</p-dialog>

<p-dialog [resizable]="false" [draggable]="false"
  [visible]="restartFailedVisible()"
  (visibleChange)="onRestartFailedVisible($event)"
  [modal]="true"
  [style]="{ width: 'min(520px, 94vw)' }"
  [header]="'ORCH_RESTART_FAILED_AND_SKIPPED' | translate">
  <p>{{ 'ORCH_RESTART_FAILED_AND_SKIPPED_PROCESS_CONFIRMATION' | translate }}</p>
  <ng-template #footer>
    <app-secondary-button
      title="Keep as-is"
      [disable]="restartFailedSaving()"
      (action)="closeRestartFailed()"
    ></app-secondary-button>
    <app-primary-button
      title="ORCH_RESTART_FAILED_AND_SKIPPED"
      icon="pi pi-replay"
      [loading]="restartFailedSaving()"
      (action)="confirmRestartFailed()"
    ></app-primary-button>
  </ng-template>
</p-dialog>

<p-dialog [resizable]="false" [draggable]="false"
  [visible]="capVisible()"
  (visibleChange)="onCapDialogVisible($event)"
  [modal]="true"
  [style]="{ width: 'min(520px, 94vw)' }"
  [header]="'Edit assignment cap' | translate">
  <p>{{ 'Concurrent assignment cap hint' | translate }}</p>
  @if (capError()) {
    <div class="error-message">{{ capError() | translate }}</div>
  }
  <msf-input
    [model]="capInput()"
    (modelChange)="onCapInput($event)"
    [placeholder]="'Inherit process default' | translate"
    [label]="'Concurrent assignment caps' | translate"
  ></msf-input>
  <ng-template #footer>
    <app-secondary-button
      title="Keep as-is"
      [disable]="capSaving()"
      (action)="closeEditCap()"
    ></app-secondary-button>
    <app-primary-button
      title="Save"
      [loading]="capSaving()"
      (action)="confirmEditCap()"
    ></app-primary-button>
  </ng-template>
</p-dialog>

<p-dialog [resizable]="false" [draggable]="false"
  [visible]="tagsVisible()"
  (visibleChange)="onTagsDialogVisible($event)"
  [modal]="true"
  [style]="{ width: 'min(520px, 94vw)' }"
  [header]="'Edit worker tags' | translate">
  <p>{{ 'Worker tag hint' | translate }}</p>
  @if (tagsError()) {
    <div class="error-message">{{ tagsError() | translate }}</div>
  }
  <app-checkbox
    [label]="'Any worker'"
    [ngModel]="tagsAnyWorker()"
    (ngModelChange)="onTagsAnyWorker($event)"
  ></app-checkbox>
  <app-worker-tag-picker
    [model]="tagsSelected()"
    [options]="tagOptions()"
    [disabled]="tagsAnyWorker()"
    (modelChange)="onTagsSelected($event)"
  ></app-worker-tag-picker>
  <ng-template #footer>
    <app-secondary-button
      title="Keep as-is"
      [disable]="tagsSaving()"
      (action)="closeEditTags()"
    ></app-secondary-button>
    <app-primary-button
      title="Save"
      [loading]="tagsSaving()"
      (action)="confirmEditTags()"
    ></app-primary-button>
  </ng-template>
</p-dialog>

<p-dialog [resizable]="false" [draggable]="false"
  [visible]="failureVisible()"
  (visibleChange)="onFailureDialogVisible($event)"
  [modal]="true"
  [style]="{ width: 'min(520px, 94vw)' }"
  [header]="'If the assignment fails' | translate">
  <p>{{ 'Only a dead worker is restarted. A business failure stays failed. A subscription of this process uses its own values instead.' | translate }}</p>
  @if (failureError()) {
    <div class="error-message">{{ failureError() | translate }}</div>
  }
  <label class="field">
    <span>{{ 'When the run is interrupted' | translate }}</span>
    <msf-select
      [model]="failurePolicy()"
      (modelChange)="setFailurePolicy($event)"
      [options]="failureOptions"
      optionLabel="label"
      optionValue="id"
      translateBy="locale"
      [placeholder]="'When the run is interrupted' | translate"
    ></msf-select>
  </label>
  @if (failureShowsResume()) {
    <div class="field">
      <msf-input
        [model]="failureResume()"
        (modelChange)="setFailureResume($event)"
        type="number"
        [min]="1"
        [label]="'Resume attempts' | translate"
      ></msf-input>
      <small class="parameter-help">{{ 'How many times to continue the same assignment after the worker exits.' | translate }}</small>
    </div>
  }
  <div class="field">
    <msf-input
      [model]="failurePause()"
      (modelChange)="setFailurePause($event)"
      type="number"
      [min]="0"
      [label]="'Pause after consecutive failures' | translate"
    ></msf-input>
    <small class="parameter-help">{{ 'Stored on the process. A subscription of this process replaces all three values.' | translate }}</small>
  </div>
  <ng-template #footer>
    <app-secondary-button
      title="Keep as-is"
      [disable]="failureSaving()"
      (action)="closeEditFailure()"
    ></app-secondary-button>
    <app-primary-button
      title="Save"
      [loading]="failureSaving()"
      (action)="confirmEditFailure()"
    ></app-primary-button>
  </ng-template>
</p-dialog>

<p-dialog [resizable]="false" [draggable]="false"
  [visible]="cancellationVisible()"
  (visibleChange)="cancellationVisible.set($event)"
  [modal]="true"
  [style]="{ width: 'min(520px, 94vw)' }"
  [header]="'Cancel assignment' | translate">
  <p>{{ 'Cancel assignment confirmation' | translate }}</p>
  <ng-template #footer>
    <app-secondary-button
      title="Keep running"
      [disable]="cancellationSaving()"
      (action)="closeCancellation()"
    ></app-secondary-button>
    <app-primary-button
      title="Cancel assignment"
      icon="pi pi-stop-circle"
      [loading]="cancellationSaving()"
      (action)="confirmCancellation()"
    ></app-primary-button>
  </ng-template>
</p-dialog>
